import { zodResolver } from '@hookform/resolvers/zod'
import { AtSign, Info, KeyRound, Loader2, LockKeyhole, Mail, Phone, RotateCcw, Save, ShieldCheck, UserRound } from 'lucide-react'
import { useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { toast } from 'sonner'
import { z } from 'zod'
import { Field, FormSection } from '@/components/common/FormBits'
import { PageHeader } from '@/components/common/PageHeader'
import { Avatar } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { PhoneInput } from '@/components/ui/phone-input'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { apiErrorMessage } from '@/i18n'
import { isApiError } from '@/lib/api/errors'
import { useAuth } from '@/lib/auth/AuthProvider'
import { formatTrPhone, isCompleteTrPhone } from '@/lib/phone'
import { PasswordInput, PasswordStrength } from './PasswordField'
import { PasswordOtpStep } from './PasswordOtpStep'
import { PASSWORD_RULES } from './passwordRules'

const TABS = ['profile', 'security'] as const
type Tab = (typeof TABS)[number]

function Card({ icon: Icon, title, hint, children }: { icon: typeof UserRound; title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="min-w-0 rounded-2xl border bg-card shadow-card">
      <header className="flex items-start gap-3 border-b px-4 py-4 sm:px-6">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Icon className="size-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="font-semibold">{title}</h2>
          {hint ? <p className="mt-0.5 text-sm text-muted-foreground">{hint}</p> : null}
        </div>
      </header>
      <div className="p-4 sm:p-6">{children}</div>
    </section>
  )
}

export function AccountPage() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const tab: Tab = (TABS as readonly string[]).includes(params.get('tab') ?? '') ? (params.get('tab') as Tab) : 'profile'
  const setTab = (v: Tab) =>
    setParams(
      (prev) => {
        const n = new URLSearchParams(prev)
        if (v === 'profile') n.delete('tab')
        else n.set('tab', v)
        return n
      },
      { replace: true },
    )
  if (!user) return null
  const name = `${user.first_name} ${user.last_name}`.trim() || user.email

  return (
    <>
      <PageHeader title={t('account.title')} description={t('account.subtitle')} />

      <div className="space-y-4">
        <section className="flex flex-col gap-4 rounded-2xl border bg-card p-4 shadow-card sm:flex-row sm:items-center sm:p-6">
          <Avatar name={name} src={user.avatar_url} className="size-16 text-lg sm:size-20 [&_span]:text-xl" />
          <div className="min-w-0 flex-1 space-y-1.5">
            <p className="truncate text-lg font-semibold">{name}</p>
            <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="inline-flex min-w-0 items-center gap-1.5">
                <Mail className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{user.email}</span>
              </span>
              {user.phone ? (
                <span className="inline-flex items-center gap-1.5">
                  <Phone className="size-4 shrink-0" aria-hidden />
                  {user.phone}
                </span>
              ) : null}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {user.roles.map((r) => (
                <Badge key={r.id} tone="primary">
                  {r.name}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)}>
          <TabsList>
            <TabsTrigger value="profile">
              <UserRound />
              {t('account.tabs.profile')}
            </TabsTrigger>
            <TabsTrigger value="security">
              <ShieldCheck />
              {t('account.tabs.security')}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === 'profile' ? <ProfileForm key={`${user.email}-${user.phone ?? ''}-${user.first_name}-${user.last_name}`} /> : <SecurityForm />}
      </div>
    </>
  )
}

function ProfileForm() {
  const { t } = useTranslation()
  const { user, updateProfile } = useAuth()
  // Personele bağlı hesapta ad, soyad ve e-posta İK kaydından gelir (sunucu da reddeder).
  const hrManaged = !!user?.employee_id
  const schema = z
    .object({
      first_name: z.string().trim().min(2, t('account.errors.firstName')).max(50),
      last_name: z.string().trim().min(2, t('account.errors.lastName')).max(50),
      phone: z.string().refine((v) => !v || isCompleteTrPhone(v), t('account.errors.phone')),
      email: z.string().trim().email(t('auth.invalidEmail')),
      current_password: z.string(),
    })
    .refine((v) => v.email === user?.email || v.current_password.length > 0, { path: ['current_password'], message: t('account.errors.currentForEmail') })
  const defaults = { first_name: user?.first_name ?? '', last_name: user?.last_name ?? '', phone: user?.phone ? formatTrPhone(user.phone) : '', email: user?.email ?? '', current_password: '' }
  const form = useForm({ resolver: zodResolver(schema), defaultValues: defaults })
  const { errors, isDirty, isSubmitting } = form.formState
  const email = useWatch({ control: form.control, name: 'email' })
  const emailChanged = email.trim() !== (user?.email ?? '')

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await updateProfile({ first_name: v.first_name.trim(), last_name: v.last_name.trim(), phone: v.phone, email: v.email.trim(), current_password: emailChanged ? v.current_password : undefined })
      toast.success(emailChanged ? t('account.savedEmail', { email: v.email.trim() }) : t('account.saved'))
    } catch (e) {
      if (isApiError(e) && e.code === 'CURRENT_PASSWORD_REQUIRED') form.setError('current_password', { message: t('account.errors.currentForEmail') })
      else toast.error(apiErrorMessage(e))
    }
  })

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Card icon={UserRound} title={t('account.profile.title')} hint={t('account.profile.hint')}>
        <div className="space-y-6">
          <FormSection>
            <Field id="ac-first" label={t('account.fields.firstName')} error={errors.first_name?.message}>
              <Input id="ac-first" readOnly={hrManaged} autoComplete="given-name" aria-invalid={!!errors.first_name} {...form.register('first_name')} />
            </Field>
            <Field id="ac-last" label={t('account.fields.lastName')} error={errors.last_name?.message}>
              <Input id="ac-last" readOnly={hrManaged} autoComplete="family-name" aria-invalid={!!errors.last_name} {...form.register('last_name')} />
            </Field>
            <Field id="ac-phone" label={t('account.fields.phone')} error={errors.phone?.message} hint={t('account.profile.phoneHint')}>
              <Controller control={form.control} name="phone" render={({ field }) => <PhoneInput id="ac-phone" autoComplete="tel" value={field.value} onChange={field.onChange} onBlur={field.onBlur} aria-invalid={!!errors.phone} />} />
            </Field>
            <Field id="ac-email" label={t('account.fields.email')} error={errors.email?.message}>
              <div className="relative">
                <AtSign className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                <Input id="ac-email" readOnly={hrManaged} type="email" autoComplete="email" className="pl-8" aria-invalid={!!errors.email} {...form.register('email')} />
              </div>
            </Field>
          </FormSection>

          {hrManaged ? (
            <p className="flex items-start gap-2 text-xs text-muted-foreground">
              <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              {t('account.profile.hrManaged')}
            </p>
          ) : null}

          {emailChanged ? (
            <div className="space-y-3 rounded-xl border border-warning/40 bg-warning/[0.08] p-4">
              <p className="flex items-start gap-2 text-sm">
                <Info className="mt-0.5 size-4 shrink-0 text-warning-strong" aria-hidden />
                {t('account.profile.emailNotice')}
              </p>
              <Field id="ac-current" label={t('auth.changePassword.current')} error={errors.current_password?.message} className="max-w-sm">
                <PasswordInput id="ac-current" autoComplete="current-password" aria-invalid={!!errors.current_password} {...form.register('current_password')} />
              </Field>
            </div>
          ) : null}

          <div className="flex flex-col-reverse gap-2 border-t pt-4 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" disabled={!isDirty || isSubmitting} onClick={() => form.reset(defaults)}>
              <RotateCcw />
              {t('account.reset')}
            </Button>
            <Button type="submit" disabled={!isDirty || isSubmitting}>
              {isSubmitting ? <Loader2 className="animate-spin" /> : <Save />}
              {t('account.save')}
            </Button>
          </div>
        </div>
      </Card>
    </form>
  )
}

function SecurityForm() {
  const { t } = useTranslation()
  const { requestPasswordOtp, changePassword } = useAuth()
  const [pre, setPre] = useState<string | null>(null)
  const [otp, setOtp] = useState('')
  const [otpError, setOtpError] = useState<string>()
  const [resending, setResending] = useState(false)
  const schema = z
    .object({
      current_password: z.string().min(1, t('auth.passwordRequired')),
      new_password: z.string().refine((v) => PASSWORD_RULES.every((r) => r.test(v)), t('account.errors.weak')),
      confirm: z.string().min(1, t('account.errors.confirm')),
    })
    .refine((v) => v.new_password === v.confirm, { message: t('auth.changePassword.mismatch'), path: ['confirm'] })
    .refine((v) => v.new_password !== v.current_password, { message: t('account.errors.sameAsCurrent'), path: ['new_password'] })
  const form = useForm({ resolver: zodResolver(schema), defaultValues: { current_password: '', new_password: '', confirm: '' } })
  const { errors, isSubmitting } = form.formState
  const next = useWatch({ control: form.control, name: 'new_password' })

  const onSubmit = form.handleSubmit(async (v) => {
    try {
      if (!pre) {
        setPre(await requestPasswordOtp(v.current_password, v.new_password))
        return
      }
      if (!/^\d{6}$/.test(otp)) {
        setOtpError(t('auth.otpInvalid'))
        return
      }
      // Başarıda sunucu tüm oturumları kapatır; kullanıcı yeni parolayla giriş ekranına döner.
      await changePassword(pre, otp, v.new_password)
      toast.success(t('account.passwordChanged'))
    } catch (e) {
      toast.error(apiErrorMessage(e))
    }
  })

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
      <form onSubmit={(e) => void onSubmit(e)} noValidate>
        <Card icon={KeyRound} title={t('account.security.title')} hint={t('auth.changePassword.voluntarySubtitle')}>
          <div className="mx-auto max-w-md space-y-4 lg:mx-0">
            <Field id="sc-current" label={t('auth.changePassword.current')} error={errors.current_password?.message}>
              <PasswordInput id="sc-current" readOnly={!!pre} autoComplete="current-password" aria-invalid={!!errors.current_password} {...form.register('current_password')} />
            </Field>
            <Field id="sc-next" label={t('auth.changePassword.next')} error={errors.new_password?.message}>
              <PasswordInput id="sc-next" readOnly={!!pre} autoComplete="new-password" aria-invalid={!!errors.new_password} {...form.register('new_password')} />
            </Field>
            <PasswordStrength value={next} />
            <Field id="sc-confirm" label={t('auth.changePassword.confirm')} error={errors.confirm?.message}>
              <PasswordInput id="sc-confirm" readOnly={!!pre} autoComplete="new-password" aria-invalid={!!errors.confirm} {...form.register('confirm')} />
            </Field>
            {pre ? (
              <PasswordOtpStep
                id="sc-otp"
                value={otp}
                onChange={(v) => {
                  setOtp(v)
                  setOtpError(undefined)
                }}
                error={otpError}
                resending={resending}
                onResend={() => {
                  setResending(true)
                  const v = form.getValues()
                  requestPasswordOtp(v.current_password, v.new_password)
                    .then(setPre)
                    .catch((e: unknown) => toast.error(apiErrorMessage(e)))
                    .finally(() => setResending(false))
                }}
              />
            ) : null}
            <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="animate-spin" /> : <LockKeyhole />}
              {pre ? t('auth.changePassword.submit') : t('account.sendCode')}
            </Button>
          </div>
        </Card>
      </form>
      <aside className="h-fit space-y-3 rounded-2xl border border-primary/15 bg-primary/[0.04] p-4 text-sm sm:p-5">
        <p className="flex items-center gap-2 font-semibold">
          <ShieldCheck className="size-4 text-primary" aria-hidden />
          {t('account.security.tipsTitle')}
        </p>
        <ul className="list-disc space-y-1.5 pl-5 text-muted-foreground">
          <li>{t('account.security.tip1')}</li>
          <li>{t('account.security.tip2')}</li>
          <li>{t('account.security.tip3')}</li>
        </ul>
        <p className="border-t pt-3 text-xs text-muted-foreground">{t('auth.security')}</p>
      </aside>
    </div>
  )
}
